import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** Notification template (stored in localStorage or ready for display) */
export interface ITemplate {
  id: number;
  key: string;
  subject: string;
  author?: string;
  description?: string;
  contentHTML: string;
  time: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** localStorage key for user-created templates array */
export const CREATED_TEMPLATES_KEY = 'created-e-notification-templates';

/** localStorage key prefix for built-in template variants (e.g. e-notification-template-1) */
export const TEMPLATE_VARIANTS_PREFIX = 'e-notification-template-';

// ─── Format ──────────────────────────────────────────────────────────────────

/** Formats time string to "2:14 pm". Accepts ISO string or "h:mm a" format. */
export function formatTime(timeString: string): string {
  const parsedAsIso = dayjs(timeString);

  if (parsedAsIso.isValid()) return parsedAsIso.format('h:mm a');
  const parsedAsTimeOnly = dayjs(timeString, 'h:mm a');

  return parsedAsTimeOnly.isValid() ? parsedAsTimeOnly.format('h:mm a') : timeString;
}

// ─── Storage ─────────────────────────────────────────────────────────────────

/** Maps template data to display format (adds author, description) */
export function toEmail(template: ITemplate, templateKey: string): ITemplate {
  return {
    ...template,
    key: templateKey,
    author: template.author ?? 'Guidance Financial Institution',
    description: template.description ?? '',
  };
}

/** Loads user-created templates from localStorage */
export function loadCreatedTemplates(): ITemplate[] {
  try {
    const storedJson = localStorage.getItem(CREATED_TEMPLATES_KEY);
    const parsedTemplates = storedJson ? (JSON.parse(storedJson) as ITemplate[]) : [];

    return Array.isArray(parsedTemplates) ? parsedTemplates : [];
  } catch {
    return [];
  }
}

/** Loads built-in template variants from localStorage */
export function loadTemplateVariants(): ITemplate[] {
  const emailItems: ITemplate[] = [];
  const variantKeys = Object.keys(localStorage).filter(key => key.startsWith(TEMPLATE_VARIANTS_PREFIX));

  variantKeys.forEach(variantKey => {
    const storedJson = localStorage.getItem(variantKey);

    if (!storedJson) return;
    try {
      const template = JSON.parse(storedJson) as ITemplate;

      emailItems.push(toEmail(template, variantKey));
    } catch {
      // skip invalid entries
    }
  });

  return emailItems;
}

/** Loads all emails: created first, then variants */
export function loadEmails(): ITemplate[] {
  const createdEmails = loadCreatedTemplates().map(template => toEmail(template, template.key));
  const variantEmails = loadTemplateVariants();

  return [...createdEmails, ...variantEmails];
}
